import { CloseFullscreen, OpenInFull, PictureInPicture } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import { useDispatch } from 'react-redux';
import { useMobileApp } from '../../hooks/useMobileApp';
import useMobileView from '../../hooks/useMobileView';
import { StageComponentInterface } from '../../interfaces/pagebuilder';
import { setReactPlayerPip } from '../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../reduxToolkit/typedRedux';
import useOnScreen from '../../utils/useOnScreen';

interface VyReactPlayerInterface {
  /** props for the react player */
  props: ReactPlayerProps;
  /**
   * this is used to assign an id for the pip state
   */
  uiComponent?: StageComponentInterface;
  /** props for the wrapper div of the react player */
  wrapperProps?: any;
  /** used to check the isPippable setting */
  stageSettings?: any;
  playerStyle?: CSSProperties;
  onCustomFullScreen?: (time?: number, isPlaying?: boolean) => void;
  isFullScreen?: boolean;
  openCustomFullScreen?: boolean;
  currentTime?: number;
}
const VyReactPlayer = ({
  props,
  uiComponent,
  wrapperProps,
  stageSettings,
  playerStyle,
  onCustomFullScreen,
  isFullScreen = false,
  openCustomFullScreen = false,
  currentTime,
}: VyReactPlayerInterface) => {
  const canPip = uiComponent?.stageSettings?.isPippable || stageSettings?.isPippable;

  const reactPlayerRef: RefObject<ReactPlayer> = useRef(null);
  const reactPlayerWrapperRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(reactPlayerWrapperRef);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPip, setIsPip] = useState(false);
  const [isAutoPip] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [playerId, setPlayerId] = useState('');
  const [seekToTime, setSeekToTime] = useState<number>();
  const pagedata = useSelector((state) => state.common.pagedata);
  const reactPlayerPip = useSelector((state) => state.common.reactPlayerPip);
  const isTabUsedElsewhere = useSelector(({ common }) => common.isTabUsedElsewhere);
  const liveStream = useSelector((state) => state.common.livestreams.find((ls) => ls._id === stageSettings?.vystemstream?._id));
  const event = useSelector(({ common }) => common.event);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isMobileView } = useMobileView();
  const { isAppView } = useMobileApp();

  /** sets a player id to be identified by pip state in redux */
  useEffect(() => {
    setPlayerId(`player-${uiComponent?._id || pagedata?._id || 'undefined'}`);
  }, [uiComponent, pagedata]);

  /**
   * initialize isPip local state
   * useful to preserve the right state after navigation
   */
  useEffect(() => {
    if (playerId === reactPlayerPip.id) setIsPip(reactPlayerPip.isOpen);
  }, [playerId]);

  /**
   * triggered when visibility state for the react player changes
   * if the local state of pip is true and the component is not
   * visible anymore, triggers a dispatch to redux pip state to show the pip video
   */
  useEffect(() => {
    let unmounted = false;
    if (isPip) return;
    if (!unmounted)
      if (!isVisible && reactPlayerRef.current && isPlaying && isAutoPip) {
        dispatch(
          setReactPlayerPip({ isOpen: true, props: reactPlayerRef.current.props, currentTime: reactPlayerRef.current?.getCurrentTime() || 0, id: playerId })
        );
      }
    return () => {
      unmounted = true;
    };
  }, [isVisible]);

  /**
   * triggered whenever the redux pip isOpen state is changed
   * if the redux pip state has the same id as this player
   * then apply the change locally
   */
  useEffect(() => {
    let unmounted = false;
    if (!unmounted) console.log('PLAYER ID : ', playerId);
    if (reactPlayerPip?.id === playerId) {
      setIsPip(reactPlayerPip?.isOpen);
    }
    return () => {
      unmounted = true;
    };
  }, [reactPlayerPip?.isOpen]);

  /**
   * triggered whenever a redux or local state changes
   * in a direction to close pip
   */
  useEffect(() => {
    let unmounted = false;
    if (!unmounted)
      if (!isPip && isReady && reactPlayerPip?.id === playerId && !reactPlayerPip?.isOpen) {
        reactPlayerRef?.current?.seekTo(reactPlayerPip?.currentTime || 0, 'seconds');
        setIsPlaying(false);
        setIsPip(false);
        dispatch(setReactPlayerPip({ ...reactPlayerPip, id: '' }));
      }
    return () => {
      unmounted = true;
    };
  }, [isPip, isReady, reactPlayerPip?.isOpen, reactPlayerPip.currentTime]);

  /**
   * resets isReady state whenever the pip state changes
   * this state will be automatically set to true when the player is ready to play
   */
  useEffect(() => {
    let unmounted = false;
    if (!unmounted) setIsReady(false);
    return () => {
      unmounted = true;
    };
  }, [isPip]);

  /**
   * in case there are multiple stages in one page
   * this checks whenever the react player pop up id changes
   * and sets the pip local state accordingly
   */
  useEffect(() => {
    if (reactPlayerPip.id !== playerId) {
      setIsPip(false);
      setIsPlaying(false);
    } else setIsPip(true);
  }, [reactPlayerPip.id, playerId]);

  useEffect(() => setSeekToTime(currentTime), [currentTime]);

  useEffect(() => {
    if (!seekToTime || !isPlaying) return;
    reactPlayerRef.current?.seekTo(seekToTime);
    setSeekToTime(undefined);
  }, [isPlaying, seekToTime]);

  return (
    <>
      <div {...wrapperProps} style={playerStyle ?? wrapperProps?.style} className="group  relative h-full w-full" ref={reactPlayerWrapperRef}>
        {!isPip ? (
          <>
            <ReactPlayer
              {...props}
              playsinline={isAppView()}
              style={{ ...props.style, position: 'absolute', top: 0, left: 0 }}
              ref={reactPlayerRef}
              muted={isTabUsedElsewhere || (!isFullScreen && openCustomFullScreen)}
              onReady={() => {
                // useless but required parameter
                props.onReady?.(new ReactPlayer(props));
                // custom on ready behaviour
                setIsReady(true);
              }}
              onPlay={() => {
                wrapperProps?.onClick?.();
                props.onPlay?.();
                // custom on play behaviour
                setIsPlaying(true);
              }}
              onPause={() => {
                wrapperProps?.onClick?.();
                props.onPause?.();
                // custom on pause behaviour
                setIsPlaying(false);
              }}
            />
          </>
        ) : (
          <div className="bg-black w-full h-full grid place-items-center">
            <p className="text-white font-bold">{t('common:currently-playing')}</p>
          </div>
        )}
        {!isMobile && !isTablet && (
          <div className="opacity-0 group-hover:opacity-100 space-x-2 absolute top-0 right-0 h-[50px] flex flex-row justify-end items-center text-white pt-2.5 pr-[5px] transition-opacity">
            {/*<FormControlLabel
          control={<Switch defaultChecked={false} onChange={() => setIsAutoPip(!isAutoPip)} name="autopip" color="primary" />}
          label="Miniplayer"
        />*/}
            {!isFullScreen && canPip && (
              <IconButton
                onClick={() => {
                  if (isPip) {
                    dispatch(setReactPlayerPip({ ...reactPlayerPip, isOpen: false }));
                  } else {
                    dispatch(
                      setReactPlayerPip({
                        isOpen: true,
                        props: reactPlayerRef.current?.props,
                        currentTime: reactPlayerRef.current?.getCurrentTime() || 0,
                        id: playerId,
                      })
                    );
                  }
                  setIsPip(!isPip);
                }}
                style={{ outline: 'none' }}
                size="large">
                <PictureInPicture className="fixed" style={{ color: isPip ? 'var(--vystem-blue)' : 'white' }} />
              </IconButton>
            )}
            {uiComponent?.theme === 'mbw' && stageSettings?.streamType === 'Vystemstream' && liveStream?.isBroadcasting && (
              <div className="h-4 w-4 rounded-lg bg-red-800 animate-pulse" />
            )}
            {onCustomFullScreen && !isMobileView() && (
              <IconButton onClick={() => onCustomFullScreen(reactPlayerRef.current?.getCurrentTime() || 0, isPlaying)} style={{ outline: 'none' }} size="large">
                {isFullScreen ? <CloseFullscreen style={{ color: 'white' }} /> : <OpenInFull style={{ color: isPip ? 'var(--vystem-blue)' : 'white' }} />}
              </IconButton>
            )}
          </div>
        )}

        <style jsx>
          {`
            .reactplayer-wrapper {
            }
            .customcontrols-div {
            }

            .react-player__preview {
              background-size: contain !important;
            }
          `}
        </style>
      </div>
    </>
  );
};
export default VyReactPlayer;
